/**
 * HITL-1695 — the agent's display preference for the 7.0 console.
 *
 * One stored object for every display choice, so the dialog saves them together
 * and a later field is an addition rather than a new key. HITL-1695 reads
 * `transcriptStyle`; `cpCase` and `cpStyle` are defined here now so HITL-1696
 * adds the centre-pane controls without a storage migration.
 *
 * Every default is today's 7.0 console. An agent who never opens the dialog is
 * not affected by any of this, which is why no new flag gates it.
 */

/** `plain` = the HITL-1667 line; `bubble` = the 3.0 chat bubble. */
export type TranscriptStyle = 'plain' | 'bubble';
/** `menu` = the menu's own case; `caps` = CSS-uppercased. */
export type CentrePaneCase = 'menu' | 'caps';
/** `rows` = the HITL-1616 rows; `boxes` = rows painted as the 3.0 chip. */
export type CentrePaneStyle = 'rows' | 'boxes';
/**
 * HITL-1696: the option-name size the agent picked, in px. `null` = not picked,
 * so each style keeps its own (rows 13px, boxes the chip's 12px).
 */
export type CentrePaneFontSize = 12 | 13 | 14 | 15;
export const CENTRE_PANE_FONT_SIZES: CentrePaneFontSize[] = [12, 13, 14, 15];

export interface DisplayPreference {
  transcriptStyle: TranscriptStyle;
  cpCase: CentrePaneCase;
  cpStyle: CentrePaneStyle;
  cpFontSize: CentrePaneFontSize | null;
}

export const DISPLAY_PREFERENCE_VERSION = 1;

export const DEFAULT_DISPLAY_PREFERENCE: DisplayPreference = {
  transcriptStyle: 'plain',
  cpCase: 'menu',
  cpStyle: 'rows',
  cpFontSize: null,
};

const ALLOWED: { [K in keyof DisplayPreference]: DisplayPreference[K][] } = {
  transcriptStyle: ['plain', 'bubble'],
  cpCase: ['menu', 'caps'],
  cpStyle: ['rows', 'boxes'],
  cpFontSize: [...CENTRE_PANE_FONT_SIZES, null],
};

const pick = <K extends keyof DisplayPreference>(
  source: Record<string, unknown>,
  key: K
): DisplayPreference[K] => {
  const value = source[key];
  return (ALLOWED[key] as unknown[]).includes(value)
    ? (value as DisplayPreference[K])
    : DEFAULT_DISPLAY_PREFERENCE[key];
};

/**
 * Field by field, NOT all-or-nothing: one corrupt or unknown value falls back
 * to its own default and leaves the agent's other choices alone. A value a
 * later version writes is treated the same way, so a rollback never wipes the
 * fields this version does understand.
 */
export const parseDisplayPreference = (raw: unknown): DisplayPreference => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...DEFAULT_DISPLAY_PREFERENCE };
  }
  const source = raw as Record<string, unknown>;
  return {
    transcriptStyle: pick(source, 'transcriptStyle'),
    cpCase: pick(source, 'cpCase'),
    cpStyle: pick(source, 'cpStyle'),
    cpFontSize: pick(source, 'cpFontSize'),
  };
};
